import { useEffect, useRef, useState, useMemo } from 'react'
import './RefreshMethodContextMenu.css'

const REFRESH_METHODS = [
  { id: 'auto', label: 'Automatique', description: 'Choisit la meilleure méthode', icon: '/Icon auto awesome.png' },
  { id: 'extension', label: 'Extension navigateur', description: 'Utilise l\'extension installée', icon: '/Icon chrome.png' },
  { id: 'client', label: 'Extraction client', description: 'Scraping depuis votre navigateur', icon: '/Icon wifi.png' },
  { id: 'server', label: 'Extraction serveur', description: 'Scraping depuis le serveur', icon: '/Icon server.png' }
]

function RefreshMethodContextMenu({ isOpen, position, onClose, onSelectMethod, currentMethod = 'auto', skipWhitelist = false, onToggleWhitelist }) {
  const menuRef = useRef(null)
  const toggleIdRef = useRef(`whitelist-toggle-${Math.random().toString(36).substr(2, 9)}`)
  const [hoveredIndex, setHoveredIndex] = useState(-1)
  
  // Calculer la position ajustée de manière synchrone AVANT le rendu pour éviter le glissement
  const initialAdjustedPosition = useMemo(() => {
    if (!isOpen || !position || position.x === undefined || position.y === undefined) {
      return position || { x: 0, y: 0 }
    }

    // Estimer les dimensions du menu (280-320px de large, ~400px de haut environ)
    const estimatedMenuWidth = 300
    const estimatedMenuHeight = 400
    const windowWidth = typeof window !== 'undefined' ? window.innerWidth : 1920
    const windowHeight = typeof window !== 'undefined' ? window.innerHeight : 1080

    let newX = position.x
    let newY = position.y

    // Ajuster horizontalement avec les dimensions estimées
    if (newX + estimatedMenuWidth > windowWidth) {
      newX = windowWidth - estimatedMenuWidth - 10
    }
    if (newX < 10) {
      newX = 10
    }

    // Ajuster verticalement avec les dimensions estimées
    if (newY + estimatedMenuHeight > windowHeight) {
      newY = windowHeight - estimatedMenuHeight - 10
    }
    if (newY < 10) {
      newY = 10
    }

    // S'assurer que la position est toujours dans les limites
    newX = Math.max(10, Math.min(newX, windowWidth - estimatedMenuWidth - 10))
    newY = Math.max(10, Math.min(newY, windowHeight - estimatedMenuHeight - 10))

    return { x: newX, y: newY }
  }, [isOpen, position])

  // Utiliser directement la position calculée initialement
  const [finalPosition, setFinalPosition] = useState(initialAdjustedPosition)
  const [isReady, setIsReady] = useState(false)

  useEffect(() => {
    if (!isOpen) {
      setIsReady(false)
      return
    }

    // Initialiser avec la position calculée immédiatement
    setFinalPosition(initialAdjustedPosition)
    
    // Rendre le menu visible après que React ait rendu avec la bonne position
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        setIsReady(true)
      })
    })

    // Ajuster finement après le rendu avec les dimensions réelles (seulement si nécessaire)
    const adjustPosition = () => {
      const menu = menuRef.current
      if (!menu) return

      requestAnimationFrame(() => {
        const menuRect = menu.getBoundingClientRect()
        const windowWidth = window.innerWidth
        const windowHeight = window.innerHeight

        // Si le menu n'a pas encore de dimensions, réessayer plus tard
        if (menuRect.width === 0 || menuRect.height === 0) {
          setTimeout(adjustPosition, 50)
          return
        }

        let newX = position.x
        let newY = position.y

        // Ajuster horizontalement
        if (menuRect.right > windowWidth) {
          newX = windowWidth - menuRect.width - 10
        }
        if (menuRect.left < 0) {
          newX = 10
        }

        // Ajuster verticalement
        if (menuRect.bottom > windowHeight) {
          newY = windowHeight - menuRect.height - 10
        }
        if (menuRect.top < 0) {
          newY = 10
        }

        // S'assurer que la position est toujours positive et dans les limites
        newX = Math.max(10, Math.min(newX, windowWidth - menuRect.width - 10))
        newY = Math.max(10, Math.min(newY, windowHeight - menuRect.height - 10))

        // Mettre à jour seulement si la position a significativement changé (plus de 10px)
        const currentX = finalPosition.x
        const currentY = finalPosition.y
        if (Math.abs(newX - currentX) > 10 || Math.abs(newY - currentY) > 10) {
          setFinalPosition({ x: newX, y: newY })
        }
      })
    }

    // Ajuster finement après un délai plus long (seulement pour les ajustements fins)
    const adjustTimeout = setTimeout(adjustPosition, 300)

    // Fermer le menu lors du scroll
    const handleScroll = () => {
      onClose()
    }

    window.addEventListener('scroll', handleScroll, true)

    return () => {
      clearTimeout(adjustTimeout)
      window.removeEventListener('scroll', handleScroll, true)
    }
  }, [isOpen, position, onClose, initialAdjustedPosition, finalPosition])

  useEffect(() => {
    if (!isOpen) return

    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        onClose()
      }
    }

    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        onClose()
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    document.addEventListener('keydown', handleEscape)

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
      document.removeEventListener('keydown', handleEscape)
    }
  }, [isOpen, onClose])

  if (!isOpen) return null

  // Utiliser la position finale (ajustée finement) ou la position initiale
  const displayPosition = finalPosition && finalPosition.x !== undefined && finalPosition.y !== undefined 
    ? finalPosition 
    : initialAdjustedPosition

  return (
    <div
      ref={menuRef}
      className="refresh-method-context-menu glass-container"
      style={{
        position: 'fixed',
        left: `${displayPosition.x || 0}px`,
        top: `${displayPosition.y || 0}px`,
        zIndex: 10000,
        // Désactiver les transitions pour éviter le glissement
        transition: 'none',
        // Cacher le menu jusqu'à ce que la position soit prête
        visibility: isReady ? 'visible' : 'hidden',
        opacity: isReady ? 1 : 0,
        pointerEvents: isReady ? 'auto' : 'none'
      }}
    >
      <div className="glass-filter"></div>
      <div className="glass-overlay"></div>
      <div className="glass-specular"></div>
      <div className="glass-content">
        <div className="context-menu-header">
          <span>Méthode d'actualisation</span>
        </div>
        <div className="context-menu-items">
          {/* Toggle pour désactiver la whitelist */}
          <div className="context-menu-item whitelist-toggle-item" onClick={(e) => e.stopPropagation()}>
            <div className="method-content">
              <div className="method-text" style={{ flex: 1 }}>
                <div className="method-label">Extraire sans whitelist</div>
                <div className="method-description">Permet d'extraire de n'importe quel site</div>
              </div>
              <div className="toggle-container">
                <input 
                  type="checkbox" 
                  id={toggleIdRef.current}
                  className="toggle-checkbox"
                  checked={skipWhitelist}
                  onChange={(e) => {
                    e.stopPropagation()
                    if (onToggleWhitelist) {
                      onToggleWhitelist(e.target.checked)
                    }
                  }}
                />
                <label 
                  htmlFor={toggleIdRef.current}
                  className={`toggle-label ${skipWhitelist ? 'checked' : ''}`}
                  onClick={(e) => {
                    e.stopPropagation()
                    // Le label déclenche automatiquement le changement via htmlFor
                  }}
                ></label>
              </div>
            </div>
          </div>
          <div className="context-menu-divider"></div>
          {REFRESH_METHODS.map((method, index) => (
            <div
              key={method.id}
              className={`context-menu-item ${currentMethod === method.id ? 'selected' : ''} ${hoveredIndex === index ? 'hovered' : ''}`}
              onClick={() => {
                onSelectMethod(method.id)
                onClose()
              }}
              onMouseEnter={() => setHoveredIndex(index)}
              onMouseLeave={() => setHoveredIndex(-1)}
            >
              <div className="method-content">
                <div className="method-icon-wrapper">
                  <img src={method.icon} alt={method.label} className="method-icon" />
                </div>
                <div className="method-text">
                  <div className="method-label">{method.label}</div>
                  <div className="method-description">{method.description}</div>
                </div>
                {currentMethod === method.id && (
                  <div className="method-check">✓</div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

export default RefreshMethodContextMenu

